<template>
  <div class="order-detail-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">‹ 返回</button>
      <div class="page-title">订单详情</div>
    </div>

    <!-- 订单状态 -->
    <div class="order-status-bar">
      <div class="status-badge" :style="{ backgroundColor: getStatusColor(order.orderStatus) }">
        {{ getStatusText(order.orderStatus) }}
      </div>
    </div>

    <!-- 订单信息 -->
    <div class="order-section">
      <div class="section-title">订单信息</div>
      <div class="info-card">
        <div class="info-row">
          <div class="info-label">订单号</div>
          <div class="info-value">{{ order.orderNo }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">产品名称</div>
          <div class="info-value">{{ order.productName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">规格</div>
          <div class="info-value">{{ order.skuName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">出行日期</div>
          <div class="info-value">{{ formatDate(order.travelDate) }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">订单金额</div>
          <div class="info-value">¥{{ order.orderAmount }}</div>
        </div>
        <div v-if="order.discountAmount > 0" class="info-row">
          <div class="info-label">优惠</div>
          <div class="info-value discount-text">-¥{{ order.discountAmount }}</div>
        </div>
        <div class="info-row total">
          <div class="info-label">实付金额</div>
          <div class="info-value pay-text">¥{{ order.payAmount }}</div>
        </div>
      </div>
    </div>

    <!-- 出行人信息 -->
    <div v-if="order.travelers && order.travelers.length > 0" class="order-section">
      <div class="section-title">出行人信息</div>
      <div class="travelers-list">
        <div v-for="traveler in order.travelers" :key="traveler.id" class="traveler-card">
          <div class="traveler-name">{{ traveler.name }}</div>
          <div class="traveler-detail">{{ traveler.idCard }}</div>
        </div>
      </div>
    </div>

    <!-- 联系人信息 -->
    <div class="order-section">
      <div class="section-title">联系人信息</div>
      <div class="info-card">
        <div class="info-row">
          <div class="info-label">姓名</div>
          <div class="info-value">{{ order.contactName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">手机号</div>
          <div class="info-value">{{ order.contactPhone }}</div>
        </div>
      </div>
    </div>

    <!-- 时间信息 -->
    <div class="order-section">
      <div class="info-card">
        <div class="info-row">
          <div class="info-label">下单时间</div>
          <div class="info-value">{{ formatTime(order.createTime) }}</div>
        </div>
        <div v-if="order.payTime" class="info-row">
          <div class="info-label">支付时间</div>
          <div class="info-value">{{ formatTime(order.payTime) }}</div>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="order-actions">
      <button v-if="statusInfo.canPay" class="action-btn pay-btn" @click="goToPayment">去支付</button>
      <button v-if="statusInfo.canCancel" class="action-btn cancel-btn" @click="handleCancel">取消订单</button>
      <button v-if="statusInfo.canRefund" class="action-btn refund-btn" @click="handleApplyRefund">申请退款</button>
      <button v-if="order.orderStatus === 6" class="action-btn" style="background:#f59e0b;color:#fff;opacity:0.8" disabled>退款审核中</button>
    </div>
  </div>
</template>

<script>
import { getOrderDetail, applyRefund, cancelOrder, getPayStatus } from '@/api/order'
import { formatDate, formatTime, getOrderStatusText, getOrderStatusColor, getOrderStatusInfo } from '@/utils/format'

export default {
  name: 'OrderDetail',
  data() {
    return { order: {}, loading: false, pollTimer: null }
  },
  computed: {
    statusInfo() {
      return getOrderStatusInfo(this.order.orderStatus)
    }
  },
  mounted() {
    const id = this.$route.params.id
    if (id) this.loadOrderDetail(id)
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  },
  methods: {
    async loadOrderDetail(id) {
      this.loading = true
      try {
        const res = await getOrderDetail(id)
        // 后端返回 { order, travelers, priceItems, ... } 平级结构，travelers 不嵌套在 order 内
        const data = res.data || {}
        this.order = { ...(data.order || data), travelers: data.travelers || [] }
        // 从微信 H5 支付跳回时，支付回调可能尚未落库，待支付订单到达详情页时轮询确认一次
        if (this.order.orderStatus === 0 && this.order.payStatus !== 1) {
          this.pollPayStatus(id)
        }
      } catch (e) {
        console.error(e)
        this.$alert('加载失败，请重试')
      } finally {
        this.loading = false
      }
    },
    pollPayStatus(id, attempt = 0) {
      if (attempt >= 5) return
      this.pollTimer = setTimeout(async () => {
        try {
          const res = await getPayStatus(id)
          if (res.data && res.data.payStatus === 1) {
            this.loadOrderDetail(id)
          } else {
            this.pollPayStatus(id, attempt + 1)
          }
        } catch (e) {
          console.error(e)
        }
      }, 1000)
    },
    goToPayment() {
      this.$router.push({ name: 'OrderPayment', params: { id: this.order.id } })
    },
    async handleCancel() {
      const ok = await this.$confirm('确定要取消订单吗？')
      if (!ok) return
      try {
        await cancelOrder(this.order.id)
        await this.$alert('订单已取消')
        this.loadOrderDetail(this.order.id)
      } catch (e) {
        this.$alert(e.message || '取消失败')
      }
    },
    async handleApplyRefund() {
      const ok = await this.$confirm('确定要申请退款吗？提交后需等待商家审核。')
      if (!ok) return
      try {
        await applyRefund(this.order.id)
        await this.$alert('退款申请已提交，请等待审核')
        this.loadOrderDetail(this.order.id)
      } catch (e) {
        this.$alert(e.message || '申请失败，请重试')
      }
    },
    formatDate,
    formatTime,
    getStatusText: getOrderStatusText,
    getStatusColor: getOrderStatusColor
  }
}
</script>

<style lang="scss" scoped>
.order-detail-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

.page-header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 20;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #ff720d;
  cursor: pointer;
  margin-right: 12px;
  padding: 0;
}

.page-title { font-size: 16px; font-weight: 600; color: #111827; }

.order-status-bar {
  padding: 20px 16px;
  text-align: center;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.status-badge {
  display: inline-block;
  padding: 8px 20px;
  border-radius: 20px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.order-section {
  background: #fff;
  margin: 10px 0;
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
}

.info-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  &.total { padding-top: 12px; font-weight: 600; }
}

.info-label { font-size: 13px; color: #6b7280; }
.info-value { font-size: 13px; color: #111827; font-weight: 500; text-align: right; }
.discount-text { color: #10b981; }
.pay-text { font-size: 16px; font-weight: 700; color: #ff720d; }

.travelers-list { display: flex; flex-direction: column; gap: 8px; }
.traveler-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  border-left: 3px solid #ff720d;
}
.traveler-name { font-size: 14px; font-weight: 600; color: #111827; margin-bottom: 4px; }
.traveler-detail { font-size: 12px; color: #6b7280; }

.order-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  z-index: 10;
}

.action-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:active { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.pay-btn { background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%); color: #fff; }
.cancel-btn { background: #f3f4f6; color: #374151; }
.refund-btn { background: #ef4444; color: #fff; }
</style>
